import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, of, Subscription } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { DataStorage } from "src/model/class/general classes/data-storage";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { HistoryAlarm } from "src/model/class/general classes/history-alarm";
import { Logic } from "src/model/class/general classes/logic";
import { LogicLog } from "src/model/class/general classes/logic-log";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { BIGLogicKind, BIGLogicLogState } from "src/components/shared-components/enum/enumLogic";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";

@Injectable({
  providedIn: "root",
})
export class LogicsService extends DataStorage implements OnDestroy, iEvent {
  private _logContext: string = "logics.service";
  private _activeAlarmSubject: BehaviorSubject<Array<LogicLog>> =
    new BehaviorSubject<Array<LogicLog>>(null);
  private _isErrorInGetActiveAlarmListAPIRequest: boolean = false;

  get isErrorInGetActiveAlarmListAPIRequest(): boolean {
    return this._isErrorInGetActiveAlarmListAPIRequest;
  }

  public needToInitialize: boolean = true;

  public activeAlarmList = this._activeAlarmSubject.asObservable();

  _eventSubscriptions: Subscription[] = null;

  constructor(
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _storageService: LocalStorageService,
    private _loggerService: LoggerService,
    private _eventService: EventService
  ) {
    super();

    this.subscribeEvents();
  }

  /** Destroy event service */
  ngOnDestroy() {
    this._activeAlarmSubject.next(null);
    this._activeAlarmSubject.complete();

    //this.eventService.OnMakeLogout.unsubscribe();
    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_LOGICS_LOG);

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "OnMakeLogout",
          "subscribeEvents",
          " IN OnMakeLogout"
        );

        this._activeAlarmSubject.next(null);
        this.needToInitialize = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        if (this._activeAlarmSubject.isStopped) {
          this._loggerService.log(
            LogLevel.LOG_INFO,
            this._logContext,
            "OnLoginPlantDid",
            "subscribeEvents",
            "observable annullato"
          );

          this._activeAlarmSubject = new BehaviorSubject<Array<LogicLog>>(null);
          this.activeAlarmList = this._activeAlarmSubject.asObservable();
        }
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  //#region Logic -> Scenario
  /**
   *
   * @param idLogic id main logic
   * @param idScenario id scenario to assign to generic logic
   *
   * Assign scenario to generic logic
   */
  AssignScenarioToLogic(idLogic: number, idScenario: number): Observable<any> {
    const bodyRequest = {
      ids: [idScenario],
    };

    return this._http
      .post<any>(
        environment.httpServerRoot +
        AppSettings.API_URL_LOGIC +
        "/" +
        idLogic +
        "/scenarios",
        bodyRequest
      )
      .pipe(
        tap((resultSave) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SCHEDULATION_SCENARIO_CHANGED;
          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            idScenario +
            "|" +
            idLogic;
          this.publishAddress(mqttAddress, mqttMessage);
        })
      );
  }

  /**
   *
   * @param idLogic id main logic
   * @param idScenario id scenario to disassign to generic logic
   *
   * Dissassign scenario to generic logic
   */
  DeleteScenarioAssignmentsToLogic(
    idLogic: number,
    idScenario: number
  ): Observable<any> {
    const bodyRequest = {
      body: {
        ids: [idScenario],
      },
    };

    return this._http
      .request(
        "delete",
        environment.httpServerRoot +
        AppSettings.API_URL_LOGIC +
        "/" +
        idLogic +
        "/scenarios",
        bodyRequest
      )
      .pipe(
        tap((resultDelete) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SCHEDULATION_SCENARIO_CHANGED;
          const mqttMessage =
            GenericOperationKind.DELETE.toString() +
            "|" +
            idScenario +
            "|" +
            idLogic;
          this.publishAddress(mqttAddress, mqttMessage);
        })
      );
  }

  //#endregion
  /**
   *
   * @param idLogic id main logic
   * @param idCalendar id calendar to assign to generic logic
   *
   * Assign calendar to generic logi
   *
   */
  AssignCalendarToLogic(idLogic: number, idCalendar: number): Observable<any> {
    const bodyRequest = {
      ids: [idCalendar],
    };

    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_URL_LOGIC +
        "/" +
        idLogic +
        "/calendar",
        bodyRequest
      )
      .pipe(
        tap((resultSave) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SCHEDULATION_CALENDAR_CHANGED;
          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            idCalendar +
            "|" +
            idLogic;
          this.publishAddress(mqttAddress, mqttMessage);
        })
      );
  }

  publishAddress(mqttAddress: string, value: string) {
    this._mqttService.publishAddress(mqttAddress, value);
  }

  /**
   *
   * @param idLogic id main logic
   * @param idCalendar id calendar to disassign to generic logic
   *
   * Disassign calendar to generic logi
   *
   */
  DeleteCalendarAssignmentsToLogic(
    idLogic: number,
    idCalendar: number
  ): Observable<any> {
    const bodyRequest = {
      body: {
        ids: [idCalendar],
      },
    };

    return this._http
      .request(
        "delete",
        environment.httpServerRoot +
        AppSettings.API_URL_LOGIC +
        "/" +
        idLogic +
        "/calendar",
        bodyRequest
      )
      .pipe(
        tap((resultDelete) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_SCHEDULATION_CALENDAR_CHANGED;
          const mqttMessage =
            GenericOperationKind.DELETE.toString() +
            "|" +
            idCalendar +
            "|" +
            idLogic;
          this.publishAddress(mqttAddress, mqttMessage);
        })
      );
  }
  //#endregion

  //#region Get Logics
  /**
   * Get logic objects.
   */
  public async GetLogicsByKind(logicKind: BIGLogicKind) {
    const returnLogic = await this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_LOGIC
      )
      .pipe(
        switchMap((returnLogicList: GenericApiData) => {
          if (returnLogicList.data.length > 0) {
            const logics: Array<Logic> = new Array<Logic>();
            for (let i = 0; i < returnLogicList.data.length; i++) {
              if (
                logicKind == BIGLogicKind.All ||
                returnLogicList.data[i].kind == logicKind
              )
                logics.push(returnLogicList.data[i]);
            }

            return of(logics);
          } else {
            return new Array<Logic>();
          }
        })
      )
      .toPromise();

    return returnLogic;
  }
  //#endregion

  //#region Get active alarm
  /**
   *
   * Get active alarms data from API.
   *
   */
  public async GetActiveAlarms(withPopup: boolean, idBIGLogic: number) {
    let params = new HttpParams();
    params = params.append("logicKind", BIGLogicKind.Alarm);
    params = params.append("states", BIGLogicLogState.Inserted);
    params = params.append("states", BIGLogicLogState.ToSolveLater);
    params = params.append("withPopup", withPopup);
    params = params.append("idBIGLogic", idBIGLogic)
    const returnLogic = await this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_LOGIC_LOG,
        { params: params }
      )
      .pipe(
        switchMap((returnLogicList: GenericApiData) => {
          if (returnLogicList.data.length > 0) {
            let alarmsList: Array<LogicLog> = new Array<LogicLog>();
            alarmsList = [...returnLogicList.data];
            return of(alarmsList);
          } else {
            return new Array<LogicLog>();
          }
        })
      )
      .toPromise();

    return returnLogic;
  }
  //#endregion

  /**
   * Returns the alarm history filtered 
   * according to the environment and other parameters.
   * @param filter 
   */
  public async GetAlarmsHistoryByEnvironment(filter: string): Promise<any> {
    const response = await this._http
      .get<any>(
        environment.httpServerRoot + AppSettings.API_URL_LOGIC + "/historybyenvironment"
        + filter
      )
      .toPromise();

    return response || [];
  }
  //#endregion

  /**
   * Send to BIGOmnia the request of solve the logic by the current username
   * @param idLogic id of the logic to solve
   * @param username username of the current user
   */
  public SolveAlarm(idLogic: number, username: string) {
    const resetAlarmAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_ALARM_SOLVED;

    // idLogicLog|idLogic|Username|Note|localmachinename
    const mqttMessage = "0|" + idLogic + "|" + username + "||";
    this._mqttService.publishAddress(resetAlarmAddress, mqttMessage, "REFRESH");
  }

  /**
   * Send to BIGOmnia the request to take charge of the alarm by the current username
   * @param idLogic id of the alarm to take charge of
   * @param username username of the current user
   */
  public ViewAlarm(idLogic: number, username: string) {
    const viewAlarmAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_ALARM_WORKINGOVER;
    // idLogicLog|idLogic|Username|Note|localmachinename
    const mqttMessage = "0|" + idLogic + "|" + username + "||";
    this._mqttService.publishAddress(viewAlarmAddress, mqttMessage, "REFRESH");
  }
}
